import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/commercial`;
interface Summary { quotes: number; open_pipeline: number; weighted_forecast: number; total_commission: number; }

export default function Commercial() {
  const { entity } = useGlobalEntity();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [tab, setTab] = useState<'pipeline' | 'quotes' | 'commissions'>('pipeline');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [pipeForm, setPipeForm] = useState({ opportunity_name: '', customer_name: '', amount: '', stage: 'PROSPECT', expected_close_date: '' });
  const [quoteForm, setQuoteForm] = useState({ quote_number: '', customer_name: '', total_amount: '', discount_pct: '0' });
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  // Edit + delete (pipeline opportunity CRUD)
  const [editOpp, setEditOpp] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [reps, setReps] = useState<string[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await authFetch(withEntityParam(`${API}/summary`, entity)); if (sRes.ok) setSummary(await sRes.json());
      await loadTab(tab);
    } catch (e) { console.error(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  const loadTab = async (t: string) => {
    setTab(t as any);
    const url = t === 'pipeline' ? `${API}/pipeline` : t === 'quotes' ? `${API}/quotes` : `${API}/commissions`;
    const res = await authFetch(withEntityParam(url, entity)); if (res.ok) { const d = await res.json(); setData(d.opportunities || d.quotes || d.entries || []); }
  };

  const loadReps = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/pipeline/reps`);
      if (r.ok) { const d = await r.json(); setReps(d.reps || []); }
    } catch (e) { console.error(e); }
  }, []);

  const openEdit = (opp: any) => {
    setEditOpp(opp);
    setEditForm({
      opportunity_name: opp.opportunity_name || '',
      customer_name: opp.customer_name || '',
      sales_rep_name: opp.sales_rep_name || '',
      stage: opp.stage || 'PROSPECT',
      amount: String(opp.amount ?? ''),
      probability_pct: String(opp.probability_pct ?? ''),
      expected_close_date: opp.expected_close_date || '',
      source: opp.source || '',
      competitor: opp.competitor || '',
      lost_reason: opp.lost_reason || '',
    });
    loadReps();
  };

  const handleSaveEdit = async () => {
    if (!editOpp) return;
    setSavingEdit(true);
    try {
      // Only send fields that actually changed vs the loaded opp — keeps
      // PATCH payload minimal and avoids clobbering server-computed
      // probability_pct when the operator didn't touch it.
      const payload: any = {};
      const keys = ['opportunity_name', 'customer_name', 'sales_rep_name', 'stage', 'source', 'competitor', 'lost_reason', 'expected_close_date'];
      for (const k of keys) {
        const oldVal = editOpp[k] ?? '';
        const newVal = editForm[k] ?? '';
        if (String(oldVal) !== String(newVal)) payload[k] = newVal || null;
      }
      if (String(editOpp.amount ?? '') !== String(editForm.amount ?? '') && editForm.amount !== '') {
        payload.amount = parseFloat(editForm.amount) || 0;
      }
      if (String(editOpp.probability_pct ?? '') !== String(editForm.probability_pct ?? '') && editForm.probability_pct !== '') {
        payload.probability_pct = parseFloat(editForm.probability_pct) || 0;
      }
      if (Object.keys(payload).length === 0) {
        showToast('No changes to save', false); setSavingEdit(false); return;
      }
      const r = await authFetch(`${API}/pipeline/${editOpp.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        showToast('Opportunity updated', true);
        setEditOpp(null); loadTab('pipeline'); load();
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(e.detail || `HTTP ${r.status}`, false);
      }
    } catch (e: any) {
      showToast(e?.message || 'Network error', false);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      const r = await authFetch(`${API}/pipeline/${id}`, { method: 'DELETE' });
      if (r.ok) {
        showToast('Opportunity deleted', true);
        setConfirmDeleteId(null); loadTab('pipeline'); load();
      } else {
        const e = await r.json().catch(() => ({}));
        showToast(e.detail || `HTTP ${r.status}`, false);
      }
    } catch (e: any) {
      showToast(e?.message || 'Network error', false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreatePipeline = async () => {
    const payload = { ...pipeForm, amount: parseFloat(pipeForm.amount) || 0 };
    const res = await authFetch(`${API}/pipeline`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Opportunity created', true); setShowCreate(false); loadTab('pipeline'); load(); } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };
  const handleCreateQuote = async () => {
    const payload = { ...quoteForm, total_amount: parseFloat(quoteForm.total_amount) || 0, discount_pct: parseFloat(quoteForm.discount_pct) || 0 };
    const res = await authFetch(`${API}/quotes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Quote created', true); setShowCreate(false); loadTab('quotes'); load(); } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const STAGES = ['PROSPECT', 'QUALIFIED', 'PROPOSAL', 'NEGOTIATION', 'CLOSED_WON', 'CLOSED_LOST'];
  const STAGE_COLORS: Record<string, string> = { PROSPECT: 'var(--text-secondary)', QUALIFIED: 'var(--accent)', PROPOSAL: 'var(--warning)', NEGOTIATION: '#f97316', CLOSED_WON: 'var(--positive)', CLOSED_LOST: 'var(--negative)' };
  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const pipelineColumns: Column[] = [
    { key: 'opportunity_name', label: 'Opportunity', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: any) => v || '-' },
    { key: 'sales_rep_name', label: 'Rep', render: (v: any) => v || '-' },
    { key: 'stage', label: 'Stage', render: (v: any) => <span style={{ fontWeight: 700, color: STAGE_COLORS[v] || 'var(--text-secondary)', fontSize: 10 }}>{v}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'probability_pct', label: 'Probability', align: 'right', render: (v: any) => `${v}%` },
    { key: 'weighted_amount', label: 'Weighted', align: 'right', render: (v: any) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'expected_close_date', label: 'Close Date', render: (v: any) => v || '-' },
    { key: '_actions', label: '', align: 'right', width: 130, render: (_v: any, row: any) => (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(row); }}
          title="Edit opportunity"
          style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
            border: '1px solid var(--border-default)', background: 'var(--bg-card)',
            color: 'var(--text-primary)', cursor: 'pointer' }}>
          ✎ Edit
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(row.id); }}
          title="Delete opportunity"
          style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 5,
            border: '1px solid var(--negative)', background: 'var(--bg-card)',
            color: 'var(--negative)', cursor: 'pointer' }}>
          🗑
        </button>
      </div>
    ) },
  ];

  const quoteColumns: Column[] = [
    { key: 'quote_number', label: 'Quote #', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: any) => v || '-' },
    { key: 'total_amount', label: 'Amount', align: 'right', render: (v: any) => formatAmount(v) },
    { key: 'discount_pct', label: 'Discount', align: 'right', render: (v: any) => `${v}%` },
    { key: 'net_amount', label: 'Net', align: 'right', render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
  ];

  const commissionColumns: Column[] = [
    { key: 'sales_rep_name', label: 'Rep', render: (v: any) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'deal_amount', label: 'Deal Amount', align: 'right', render: (v: any) => formatAmount(v) },
    { key: 'rate', label: 'Rate', align: 'right', render: (v: any) => `${(v * 100).toFixed(1)}%` },
    { key: 'commission', label: 'Commission', align: 'right', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
  ];

  const currentColumns = tab === 'pipeline' ? pipelineColumns : tab === 'quotes' ? quoteColumns : commissionColumns;
  const emptyMessage = `No ${tab} data.`;

  return (
    <PageLayout title="Commercial" subtitle="Pipeline, quotes, commissions, and sales operations" action={{ label: tab === 'quotes' ? '+ New Quote' : '+ New Opportunity', onClick: () => setShowCreate(true) }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Quotes', value: summary.quotes },
          { label: 'Open Pipeline', value: summary.open_pipeline, color: 'var(--accent)' },
          { label: 'Weighted Forecast', value: formatAmountCompact(summary.weighted_forecast), color: 'var(--positive)' },
          { label: 'Total Commission', value: formatAmountCompact(summary.total_commission), color: 'var(--warning)' },
        ]} columns={4} />
      )}

      {/* Pipeline Stage Funnel */}
      {tab === 'pipeline' && data.length > 0 && (() => {
        const stageCounts = STAGES.map(s => ({ name: s.replace('CLOSED_', '').replace('_', ' '), count: data.filter((o: any) => o.stage === s).length, amount: data.filter((o: any) => o.stage === s).reduce((sum: number, o: any) => sum + (o.amount || 0), 0) }));
        const maxCount = Math.max(...stageCounts.map(s => s.count), 1);
        return (
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase' }}>Pipeline Funnel</div>
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 100 }}>
              {stageCounts.map((stage, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 8, color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.2 }}>{stage.name}</span>
                  <div style={{
                    width: '100%',
                    height: (stage.count / maxCount) * 60 + 10,
                    background: Object.values(STAGE_COLORS)[i] || 'var(--accent)',
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s',
                    opacity: stage.count === 0 ? 0.3 : 1,
                  }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{stage.count}</span>
                  <span style={{ fontSize: 8, color: 'var(--text-secondary)' }}>{formatAmount(stage.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <TabBar
        tabs={[
          { key: 'pipeline', label: 'Pipeline' },
          { key: 'quotes', label: 'Quotes' },
          { key: 'commissions', label: 'Commissions' },
        ]}
        active={tab}
        onChange={(key) => loadTab(key)}
      />

      <DataTable columns={currentColumns} rows={data} loading={loading} emptyMessage={emptyMessage} rowKey="id" />

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={tab === 'quotes' ? 'New Quote' : 'New Opportunity'} footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={tab === 'quotes' ? handleCreateQuote : handleCreatePipeline} /></>}>
        {tab === 'quotes' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><input placeholder="Quote Number *" value={quoteForm.quote_number} onChange={e => setQuoteForm({ ...quoteForm, quote_number: e.target.value })} style={inputStyle} /><input placeholder="Customer Name" value={quoteForm.customer_name} onChange={e => setQuoteForm({ ...quoteForm, customer_name: e.target.value })} style={inputStyle} /><div style={{ display: 'flex', gap: 8 }}><input placeholder="Total Amount" type="number" value={quoteForm.total_amount} onChange={e => setQuoteForm({ ...quoteForm, total_amount: e.target.value })} style={inputStyle} /><input placeholder="Discount %" type="number" value={quoteForm.discount_pct} onChange={e => setQuoteForm({ ...quoteForm, discount_pct: e.target.value })} style={inputStyle} /></div></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><input placeholder="Opportunity Name *" value={pipeForm.opportunity_name} onChange={e => setPipeForm({ ...pipeForm, opportunity_name: e.target.value })} style={inputStyle} /><input placeholder="Customer Name" value={pipeForm.customer_name} onChange={e => setPipeForm({ ...pipeForm, customer_name: e.target.value })} style={inputStyle} /><div style={{ display: 'flex', gap: 8 }}><input placeholder="Amount" type="number" value={pipeForm.amount} onChange={e => setPipeForm({ ...pipeForm, amount: e.target.value })} style={inputStyle} /><select value={pipeForm.stage} onChange={e => setPipeForm({ ...pipeForm, stage: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>{STAGES.map(s => <option key={s} value={s}>{s}</option>)}</select></div><div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Expected Close</label><input type="date" value={pipeForm.expected_close_date} onChange={e => setPipeForm({ ...pipeForm, expected_close_date: e.target.value })} style={inputStyle} /></div></div>
        )}
      </Modal>
      {/* ── Edit Opportunity Modal ── */}
      <Modal open={!!editOpp} onClose={() => setEditOpp(null)}
        title={editOpp ? `Edit: ${editOpp.opportunity_name}` : 'Edit Opportunity'}
        size="lg"
        footer={
          <>
            <ActionButton label="Cancel" variant="secondary" onClick={() => setEditOpp(null)} />
            <ActionButton label={savingEdit ? 'Saving…' : 'Save Changes'} onClick={handleSaveEdit} disabled={savingEdit} />
          </>
        }>
        {editOpp && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Opportunity Name</label>
              <input value={editForm.opportunity_name} onChange={e => setEditForm({ ...editForm, opportunity_name: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Customer</label>
                <input value={editForm.customer_name} onChange={e => setEditForm({ ...editForm, customer_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Sales Rep</label>
                <input list="rep-list" value={editForm.sales_rep_name}
                  onChange={e => setEditForm({ ...editForm, sales_rep_name: e.target.value })}
                  placeholder="Assign a rep…" style={inputStyle} />
                <datalist id="rep-list">
                  {reps.map(r => <option key={r} value={r} />)}
                </datalist>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Stage</label>
                <select value={editForm.stage} onChange={e => setEditForm({ ...editForm, stage: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }} aria-label="Stage">
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Amount</label>
                <input type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Probability %</label>
                <input type="number" min={0} max={100} value={editForm.probability_pct}
                  onChange={e => setEditForm({ ...editForm, probability_pct: e.target.value })}
                  placeholder={`Default: ${editOpp.probability_pct}`} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Expected Close</label>
                <input type="date" value={editForm.expected_close_date || ''} onChange={e => setEditForm({ ...editForm, expected_close_date: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Source</label>
                <input value={editForm.source} onChange={e => setEditForm({ ...editForm, source: e.target.value })} placeholder="Inbound / Referral / etc." style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Competitor</label>
                <input value={editForm.competitor} onChange={e => setEditForm({ ...editForm, competitor: e.target.value })} style={inputStyle} />
              </div>
            </div>
            {editForm.stage === 'CLOSED_LOST' && (
              <div>
                <label style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Lost Reason</label>
                <textarea value={editForm.lost_reason} onChange={e => setEditForm({ ...editForm, lost_reason: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: 8, background: 'var(--accent-subtle)', borderRadius: 6 }}>
              Weighted forecast = amount × probability, auto-recomputed on save.
              Moving to <strong>CLOSED_WON</strong> stamps <code>won_date</code>;
              <strong> CLOSED_LOST</strong> stamps <code>lost_date</code>; moving back
              to any open stage clears both.
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation ── */}
      <Modal open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)}
        title="Delete opportunity?"
        size="md"
        footer={
          <>
            <ActionButton label="Cancel" variant="secondary" onClick={() => setConfirmDeleteId(null)} />
            <ActionButton label={deleting ? 'Deleting…' : 'Delete'} variant="danger"
              disabled={deleting}
              onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)} />
          </>
        }>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          {(() => {
            const opp = data.find((o: any) => o.id === confirmDeleteId);
            if (!opp) return <span>Are you sure? This action is permanent.</span>;
            return (
              <>
                Delete <strong>{opp.opportunity_name}</strong>
                {opp.customer_name && <> for <strong>{opp.customer_name}</strong></>}
                {opp.amount > 0 && <> (<span style={{ color: 'var(--positive)' }}>{formatAmount(opp.amount)}</span>)</>}?
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                  This is a hard delete. If the deal actually closed, mark it
                  CLOSED_WON / CLOSED_LOST via Edit instead to keep the history.
                </div>
              </>
            );
          })()}
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
